import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  BarChart3, FileText, Package, Award, LayoutTemplate, Gift,
  Sparkles, LogOut, Menu, X, Columns3, CalendarDays,
  ChevronsLeft, ChevronsRight, Users, Search, Database, FileUp, MapPin,
} from "lucide-react";

const navItems = [
  { path: "/pipeline", label: "Pipeline", icon: Columns3 },
  { path: "/database", label: "Database", icon: Database },
  { path: "/imports", label: "Imports", icon: FileUp },
  { path: "/today", label: "Today", icon: CalendarDays },
  { path: "/dashboard", label: "Dashboard", icon: BarChart3 },
  { path: "/proposals", label: "Proposals", icon: FileText },
  { path: "/templates", label: "Templates", icon: LayoutTemplate },
  { path: "/packages", label: "Packages", icon: Gift },
  { path: "/addons", label: "Add-Ons", icon: Package },
  { path: "/benefits", label: "Benefits", icon: Award },
  { path: "/region-slots", label: "Region Slots", icon: MapPin },
  { path: "/users", label: "Users", icon: Users, adminOnly: true },
];

export default function AppShell({ children }) {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "true"; } catch { return false; }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  const userRole = user?.role || "viewer";
  const filteredNav = navItems.filter(item => !item.adminOnly || userRole === "admin");

  useEffect(() => {
    try { localStorage.setItem("sidebar_collapsed", String(collapsed)); } catch {}
  }, [collapsed]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close search on click outside
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) { setSearchOpen(false); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "Escape") { setSearchOpen(false); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/search?q=${encodeURIComponent(q.trim())}`, { headers: { Authorization: `Bearer ${token}` } });
      setSearchResults(r.data);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, [token]);

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => doSearch(val), 300);
  };

  const isActive = (item) => location.pathname === item.path || location.pathname.startsWith(item.path + "/");

  return (
    <div className="min-h-screen relative">
      <div className="gradient-bg" />

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-[#0A0A0A] border-r border-white/[0.06] transition-all duration-300 ${collapsed ? "w-[68px]" : "w-[240px]"}`}
        data-testid="app-sidebar"
      >
        {/* Logo */}
        <div className={`flex items-center h-14 border-b border-white/[0.04] shrink-0 ${collapsed ? "justify-center px-2" : "px-5"}`}>
          <Link to="/pipeline" className="flex items-center gap-2.5 min-w-0">
            <Sparkles className="w-5 h-5 text-[#C9A96E] shrink-0" />
            {!collapsed && <span className="font-display text-base text-white truncate">Soulful Places</span>}
          </Link>
        </div>

        {/* Search */}
        <div className={`px-2 mb-1 ${collapsed ? "" : ""}`}>
          <button
            onClick={() => setSearchOpen(true)}
            className={`w-full flex items-center gap-2.5 rounded-xl transition-all text-white/25 hover:bg-white/[0.04] hover:text-white/50 ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2"}`}
            data-testid="desktop-search-btn"
            title={collapsed ? "Search" : undefined}
          >
            <Search className="w-[18px] h-[18px] shrink-0" />
            {!collapsed && <span className="text-[13px] font-medium">Search</span>}
            {!collapsed && <span className="ml-auto text-[10px] text-white/15 font-mono">⌘K</span>}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {filteredNav.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2.5 rounded-xl transition-all duration-150 ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2"} ${active ? "bg-[#C9A96E]/10 text-[#C9A96E]" : "text-white/40 hover:bg-white/[0.04] hover:text-white/70"}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="w-[18px] h-[18px] shrink-0" />
                {!collapsed && <span className="font-medium text-[13px] truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle + User */}
        <div className="border-t border-white/[0.04] shrink-0">
          <button
            onClick={() => setCollapsed(c => !c)}
            className="w-full flex items-center justify-center py-2 text-white/20 hover:text-white/50 transition-colors"
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
          <div className={`pb-3 ${collapsed ? "px-2" : "px-3"}`}>
            {collapsed ? (
              <button
                onClick={logout}
                className="w-full flex items-center justify-center p-2 text-white/30 hover:text-white/60 hover:bg-white/[0.04] rounded-xl transition-colors"
                data-testid="logout-btn"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            ) : (
              <div className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-white/[0.03] transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C9A96E] to-[#A855F7] flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {user?.name?.charAt(0) || "A"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/70 font-medium truncate text-xs">{user?.name}</p>
                  <p className="text-white/25 text-[10px] truncate">{user?.email}</p>
                </div>
                <button onClick={logout} className="p-1 text-white/15 hover:text-white/50 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="logout-btn" title="Sign out">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/[0.06] z-40 flex items-center px-4 gap-2" data-testid="mobile-header">
        <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 text-white/60 active:text-white" data-testid="mobile-menu-toggle">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#C9A96E]" />
          <span className="font-display text-sm text-white">Soulful Places</span>
        </div>
        <div className="flex-1" />
        <button onClick={() => setSearchOpen(true)} className="p-2 text-white/40 active:text-white" data-testid="mobile-search-toggle">
          <Search className="w-4.5 h-4.5" />
        </button>
      </header>

      {/* Global Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60]" data-testid="search-overlay">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
          <div ref={searchRef} className="absolute top-0 left-0 right-0 sm:top-4 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-lg w-full bg-[#0A0A0A] sm:rounded-2xl border-b sm:border border-white/[0.08] shadow-2xl">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
              <Search className="w-4 h-4 text-white/30 shrink-0" />
              <input
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search hotels, contacts, emails..."
                className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/25"
                autoFocus
                data-testid="global-search-input"
              />
              {searching && <Sparkles className="w-4 h-4 text-[#C9A96E] animate-pulse shrink-0" />}
              <button onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults([]); }} className="p-1 text-white/30 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-[60vh] overflow-y-auto py-2">
                {searchResults.map(r => (
                  <button
                    key={r.deal_id}
                    onClick={() => { navigate(`/pipeline?open=${r.deal_id}`); setSearchOpen(false); setSearchQuery(""); setSearchResults([]); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors flex items-center gap-3"
                    data-testid={`search-result-${r.deal_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{r.deal_title}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-white/25 text-xs">
                        {r.primary_contact && <span>{r.primary_contact}</span>}
                        {r.location && <span>{r.location}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.04] text-white/30">{r.status}</span>
                      {r.matched_on.map(m => (
                        <span key={m} className="text-[8px] px-1 py-0.5 rounded bg-[#C9A96E]/10 text-[#C9A96E]/60">{m}</span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchQuery.trim().length >= 2 && searchResults.length === 0 && !searching && (
              <div className="py-8 text-center text-white/20 text-sm">No results found</div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[280px] bg-[#0A0A0A] border-r border-white/[0.06] flex flex-col animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between h-14 px-5 border-b border-white/[0.04] shrink-0">
              <div className="flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-[#C9A96E]" />
                <span className="font-display text-base text-white">Soulful Places</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 text-white/30 active:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
              {filteredNav.map((item) => {
                const active = isActive(item);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${active ? "bg-[#C9A96E]/10 text-[#C9A96E]" : "text-white/50 active:bg-white/[0.06] active:text-white"}`}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="border-t border-white/[0.04] p-4 shrink-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C9A96E] to-[#A855F7] flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {user?.name?.charAt(0) || "A"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/70 font-medium truncate text-sm">{user?.name}</p>
                  <p className="text-white/30 text-xs truncate">{user?.email}</p>
                </div>
              </div>
              <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2.5 text-white/40 active:text-white active:bg-white/[0.04] rounded-xl transition-colors" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign out</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main
        className={`relative z-10 pt-14 lg:pt-0 min-h-screen transition-all duration-300 ${collapsed ? "lg:ml-[68px]" : "lg:ml-[240px]"}`}
        data-testid="main-content"
      >
        {children}
      </main>
    </div>
  );
}
